import "../styles/application.css"

const initializeCategoryCombobox = (combobox: HTMLElement) => {
  const input = combobox.querySelector<HTMLInputElement>("[data-category-combobox-input]")
  const list = combobox.querySelector<HTMLElement>("[data-category-options]")
  const options = Array.from(combobox.querySelectorAll<HTMLElement>("[data-category-option]"))
  if (!input || !list) return

  // The native datalist remains as a no-JavaScript fallback, but would overlap
  // the custom list in browsers that display it automatically.
  input.removeAttribute("list")

  let activeOption: HTMLElement | undefined

  const visibleOptions = () => options.filter((option) => !option.hidden)

  const setActiveOption = (option?: HTMLElement) => {
    activeOption?.classList.remove("active")
    activeOption?.setAttribute("aria-selected", "false")
    activeOption = option

    if (!activeOption) {
      input.removeAttribute("aria-activedescendant")
      return
    }

    activeOption.classList.add("active")
    activeOption.setAttribute("aria-selected", "true")
    input.setAttribute("aria-activedescendant", activeOption.id)
    activeOption.scrollIntoView({ block: "nearest" })
  }

  const open = (query?: string) => {
    const normalizedQuery = query?.trim().toLocaleLowerCase() ?? ""
    options.forEach((option) => {
      option.hidden = normalizedQuery.length > 0 && !option.dataset.value?.toLocaleLowerCase().includes(normalizedQuery)
    })
    setActiveOption()
    if (options.length === 0) return
    list.hidden = false
    input.setAttribute("aria-expanded", "true")
  }

  const close = () => {
    list.hidden = true
    input.setAttribute("aria-expanded", "false")
    setActiveOption()
  }

  const select = (option: HTMLElement) => {
    input.value = option.dataset.value ?? option.textContent ?? ""
    input.dispatchEvent(new Event("change", { bubbles: true }))
    close()
    input.focus()
  }

  input.addEventListener("focus", () => open())
  input.addEventListener("click", () => open())
  input.addEventListener("input", () => open(input.value))
  input.addEventListener("keydown", (event) => {
    if (event.key === "Escape") {
      close()
      return
    }

    if (event.key === "Enter" && activeOption) {
      event.preventDefault()
      select(activeOption)
      return
    }

    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return

    event.preventDefault()
    if (list.hidden) open(input.value)

    const visible = visibleOptions()
    if (visible.length === 0) return

    const currentIndex = activeOption ? visible.indexOf(activeOption) : -1
    const nextIndex = event.key === "ArrowDown"
      ? (currentIndex + 1) % visible.length
      : (currentIndex - 1 + visible.length) % visible.length
    setActiveOption(visible[nextIndex])
  })

  options.forEach((option) => {
    option.addEventListener("pointermove", () => setActiveOption(option))
    option.addEventListener("pointerdown", (event) => {
      event.preventDefault()
      select(option)
    })
  })

  document.addEventListener("pointerdown", (event) => {
    if (event.target instanceof Node && !combobox.contains(event.target)) close()
  })
}

document.querySelectorAll<HTMLElement>("[data-category-combobox]").forEach(initializeCategoryCombobox)

const initializeBulkStatusForm = (form: HTMLFormElement) => {
  const checkboxes = Array.from(document.querySelectorAll<HTMLInputElement>("[data-bulk-select]"))
  const selectAll = document.querySelector<HTMLInputElement>("[data-bulk-select-all]")
  const count = form.querySelector<HTMLElement>("[data-bulk-selection-count]")
  const statusSelect = form.querySelector<HTMLSelectElement>("[data-bulk-status-select]")
  const submit = form.querySelector<HTMLInputElement>("[data-bulk-status-submit]")
  if (checkboxes.length === 0 || !selectAll || !count || !statusSelect || !submit) return

  let anchor: HTMLInputElement | undefined

  const updateSelection = () => {
    const selectedCount = checkboxes.filter((checkbox) => checkbox.checked).length
    count.textContent = `${selectedCount} ${selectedCount === 1 ? "report" : "reports"} selected`
    submit.disabled = selectedCount === 0 || statusSelect.value === ""
    selectAll.checked = selectedCount === checkboxes.length
    selectAll.indeterminate = selectedCount > 0 && selectedCount < checkboxes.length

    checkboxes.forEach((checkbox) => {
      checkbox.closest("tr")?.classList.toggle("selected", checkbox.checked)
    })
  }

  const selectRange = (target: HTMLInputElement, checked: boolean) => {
    if (!anchor) return

    const anchorIndex = checkboxes.indexOf(anchor)
    const targetIndex = checkboxes.indexOf(target)
    if (anchorIndex < 0 || targetIndex < 0) return

    const start = Math.min(anchorIndex, targetIndex)
    const end = Math.max(anchorIndex, targetIndex)
    checkboxes.slice(start, end + 1).forEach((checkbox) => { checkbox.checked = checked })
  }

  checkboxes.forEach((checkbox) => {
    checkbox.addEventListener("click", (event) => {
      if (event.shiftKey) selectRange(checkbox, checkbox.checked)
      else anchor = checkbox
      updateSelection()
    })
  })

  selectAll.addEventListener("change", () => {
    checkboxes.forEach((checkbox) => { checkbox.checked = selectAll.checked })
    anchor = selectAll.checked ? checkboxes[0] : undefined
    updateSelection()
  })

  statusSelect.addEventListener("change", updateSelection)

  document.addEventListener("click", (event) => {
    if (!(event.target instanceof Element)) return
    if (!event.metaKey && !event.ctrlKey && !event.shiftKey) return
    if (event.target.closest("a, button, form, input, select, textarea")) return

    const row = event.target.closest<HTMLTableRowElement>(".clickable-report-row")
    const checkbox = row?.querySelector<HTMLInputElement>("[data-bulk-select]")
    if (!checkbox) return

    event.preventDefault()
    if (event.shiftKey && anchor) {
      selectRange(checkbox, true)
    } else {
      checkbox.checked = !checkbox.checked
      anchor = checkbox
    }
    updateSelection()
  })

  updateSelection()
}

document.querySelectorAll<HTMLFormElement>("form[data-bulk-status-form]").forEach(initializeBulkStatusForm)

document.addEventListener("submit", (event) => {
  const form = event.target
  if (!(form instanceof HTMLFormElement)) return

  const message = form.dataset.confirm
  if (message && !window.confirm(message)) event.preventDefault()
})

document.addEventListener("click", (event) => {
  if (!(event.target instanceof Element)) return

  const row = event.target.closest<HTMLTableRowElement>(".clickable-report-row")
  if (!row || event.defaultPrevented || event.target.closest("a, button, form, input, select, textarea")) return
  if (window.getSelection()?.toString()) return

  const href = row.dataset.href
  if (!href) return

  if (event.ctrlKey || event.metaKey || event.shiftKey) {
    window.open(href, "_blank", "noopener")
  } else {
    window.location.assign(href)
  }
})

document.addEventListener("change", (event) => {
  if (!(event.target instanceof HTMLSelectElement)) return

  const form = event.target.closest<HTMLFormElement>("form[data-auto-submit]")
  form?.requestSubmit()
})

document.addEventListener("click", (event) => {
  if (!(event.target instanceof Element)) return

  const trigger = event.target.closest<HTMLButtonElement>("[data-lightbox-src]")
  if (trigger) {
    const dialog = document.querySelector<HTMLDialogElement>(".attachment-lightbox")
    const image = dialog?.querySelector<HTMLImageElement>(".attachment-lightbox-image")
    if (!dialog || !image || !trigger.dataset.lightboxSrc) return

    image.src = trigger.dataset.lightboxSrc
    image.alt = trigger.dataset.lightboxAlt ?? "Attachment preview"
    dialog.showModal()
    return
  }

  const closeButton = event.target.closest<HTMLButtonElement>(".attachment-lightbox-close")
  if (closeButton) {
    const dialog = closeButton.closest<HTMLDialogElement>(".attachment-lightbox")
    dialog?.close()
    dialog?.querySelector<HTMLImageElement>(".attachment-lightbox-image")?.removeAttribute("src")
    return
  }

  if (event.target instanceof HTMLDialogElement && event.target.matches(".attachment-lightbox")) {
    event.target.close()
    event.target.querySelector<HTMLImageElement>(".attachment-lightbox-image")?.removeAttribute("src")
  }
})

document.addEventListener("keydown", (event) => {
  if (event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) return
  if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return
  if (!(event.target instanceof Element)) return
  if (event.target.closest("input, textarea, select, button, [contenteditable='true']")) return

  const link = document.querySelector<HTMLAnchorElement>(`a[data-hotkey="${event.key}"]`)
  if (!link) return

  event.preventDefault()
  link.click()
})
